import React, { useEffect, useState } from "react";
import axios from "axios";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import { API } from "@/lib/lifi";

function Spark({ data, up }) {
  if (!data || !data.length) return null;
  const pts = data.map((y, i) => ({ i, y }));
  return (
    <div className="w-24 h-8">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={pts}>
          <Line type="monotone" dataKey="y" stroke={up ? "#059669" : "#FF6161"} strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function MarketsTable() {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/markets`);
        if (!cancel) setRows(r.data?.data || []);
      } catch {}
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancel = true; clearInterval(id); };
  }, []);

  return (
    <section id="markets" className="mt-12 sm:mt-16">
      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-4 gap-2">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.28em] text-[var(--accent)] font-semibold">Markets</div>
          <h2 className="text-[18px] sm:text-[22px] font-semibold text-[var(--text)] mt-1">Top assets — live pricing</h2>
        </div>
        <span className="text-[11px] text-[var(--text-muted)] font-mono">refreshes every 60s</span>
      </div>

      {/* Desktop table */}
      <div className="panel overflow-hidden hidden md:block">
        <div className="grid grid-cols-[40px_1.4fr_1fr_1fr_1.2fr_1fr_1fr] px-5 py-3 text-[10.5px] uppercase tracking-widest text-[var(--text-subtle)] border-b border-[var(--border)]">
          <div>#</div>
          <div>Asset</div>
          <div className="text-right">Price</div>
          <div className="text-right">24h</div>
          <div className="text-right">Volume (24h)</div>
          <div className="text-right">Market cap</div>
          <div className="text-right">Chart</div>
        </div>
        {rows.length === 0 && <div className="p-6 text-[13px] text-[var(--text-muted)] text-center">Loading market data…</div>}
        {rows.map((r, i) => {
          const up = (r.change24h ?? 0) >= 0;
          return (
            <div key={r.id} className="grid grid-cols-[40px_1.4fr_1fr_1fr_1.2fr_1fr_1fr] px-5 py-3.5 items-center border-b border-[var(--border)] last:border-0 hover:bg-[var(--panel-2)]">
              <div className="text-[12px] text-[var(--text-subtle)] font-mono">{String(i + 1).padStart(2, "0")}</div>
              <div className="flex items-center gap-3">
                <img src={r.image} alt="" className="w-7 h-7 rounded-full" />
                <div>
                  <div className="text-[13px] text-[var(--text)] font-medium">{r.name}</div>
                  <div className="text-[11px] text-[var(--text-muted)] font-mono">{r.symbol}</div>
                </div>
              </div>
              <div className="text-right num-md text-[var(--text)]">
                ${r.price >= 1 ? r.price.toLocaleString(undefined, { maximumFractionDigits: 2 }) : r.price?.toFixed(4)}
              </div>
              <div className={`text-right num-sm ${up ? "text-[var(--up)]" : "text-[var(--down)]"}`}>
                {up ? "+" : ""}{(r.change24h ?? 0).toFixed(2)}%
              </div>
              <div className="text-right num-sm text-[var(--text-muted)]">
                ${((r.volume24h || 0) / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 })}M
              </div>
              <div className="text-right num-sm text-[var(--text-muted)]">
                ${((r.marketCap || 0) / 1e9).toLocaleString(undefined, { maximumFractionDigits: 2 })}B
              </div>
              <div className="ml-auto"><Spark data={r.spark} up={up} /></div>
            </div>
          );
        })}
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-2">
        {rows.length === 0 && <div className="panel p-4 text-[13px] text-[var(--text-muted)] text-center">Loading market data…</div>}
        {rows.map((r, i) => {
          const up = (r.change24h ?? 0) >= 0;
          return (
            <div key={r.id} className="panel px-3 py-3 flex items-center gap-3">
              <div className="text-[11px] text-[var(--text-subtle)] font-mono w-5">{String(i + 1).padStart(2, "0")}</div>
              <img src={r.image} alt="" className="w-7 h-7 rounded-full" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[13px] text-[var(--text)] font-medium truncate">{r.name}</div>
                  <div className="num-sm text-[var(--text)]">
                    ${r.price >= 1 ? r.price.toLocaleString(undefined, { maximumFractionDigits: 2 }) : r.price?.toFixed(4)}
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <div className="text-[10.5px] text-[var(--text-muted)] font-mono">{r.symbol}</div>
                  <div className={`num-sm ${up ? "text-[var(--up)]" : "text-[var(--down)]"}`}>
                    {up ? "+" : ""}{(r.change24h ?? 0).toFixed(2)}%
                  </div>
                </div>
              </div>
              <Spark data={r.spark} up={up} />
            </div>
          );
        })}
      </div>
    </section>
  );
}
